import { useDbFunctions } from "@/talons";
import { useClient } from "./ClientContext";
import { useGlobals } from "./GlobalContext";
import { getOnStorage, removeFromStorage, setOnStorage } from "@/shared";
import React, { useState, useEffect, useContext, useCallback, createContext, useMemo, useRef } from "react";
import { useDownloadFiles } from "@/talons";

interface RCALoginInput {
  cod_pessoa: string | number;
  cod_segmento?: string | number;
  nome_usuario?: string;
}

const buildRcaData = (data: RCALoginInput) => ({
  codPessoa: data.cod_pessoa,
  codSegmento: data.cod_segmento,
  firstname: data.nome_usuario,
});

interface ClientHierarchyContextData {
  hierarchy: any[];
  setHierarchy: (hierarchy: any[]) => void;

  userType: string;

  handleRCALogin: (data: any) => void;
  handleLogoutRCA: () => void;

  selectedRCA: any | null;
  loggedUser: any | null;

  isCoordinatorActive: boolean;
  setViewType: (viewType: boolean) => void;
  loadingLogin: boolean;
  loadingLogout: boolean;
  isOpenedModalCoordinator: boolean;
  setIsOpenedModalCoordinator: (isOpened: boolean) => void;
}

const ClientHierarchyContext = createContext<ClientHierarchyContextData | undefined>(undefined);

const safeParse = <T,>(value: any, fallback: T): T => {
  if (value == null) return fallback;
  if (typeof value === "object") return value as T;
  if (typeof value !== "string") return fallback;

  const trimmed = value.trim();
  if (!trimmed || trimmed === "{}" || trimmed === "null" || trimmed === "undefined") return fallback;

  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return fallback;
  }
};

const isEmptyObject = (v: any) =>
  v && typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0;

const normalizeMaybeObject = <T,>(v: any): T | null => {
  const parsed = safeParse<T>(v, null as any);
  if (!parsed) return null;
  if (isEmptyObject(parsed)) return null;
  return parsed;
};

const ClientHierarchyProvider: React.FC<any> = ({ children }) => {
  const [viewType, setViewType] = useState<boolean>(false);
  const [userType, setUserType] = useState<string>("");
  const [hierarchy, setHierarchy] = useState<any[]>([]);
  const [selectedRCA, setSelectedRCA] = useState<any | null>(null);
  const [loggedUser, setLoggedUser] = useState<any | null>(null);
  const [loadingLogout, setLoadingLogout] = useState(false);
  const [isOpenedModalCoordinator, setIsOpenedModalCoordinator] = useState(false);

  const { loadFiles, openModalSync, loading, setError } = useDownloadFiles();
  const { deleteDB } = useDbFunctions();
  const { setLastSync, getDate } = useGlobals();
  const { setAllClients } = useClient();

  const loginInFlightRef = useRef(false);

  const isCoordinatorActive = useMemo(() => {
    return userType?.toLowerCase() === "coordenador" && !selectedRCA?.codPessoa;
  }, [selectedRCA, userType]);

  // Map pra achar o RCA rápido sem reler user_login_data
  const hierarchyByCodPessoa = useMemo(() => {
    const m = new Map<string, any>();
    (hierarchy || []).forEach((r) => {
      if (r?.cod_pessoa != null) m.set(String(r.cod_pessoa), r);
    });
    return m;
  }, [hierarchy]);

  const clearOldDatasets = useCallback(async () => {
    try {
      // limpa estado em memória também (evita UI antiga piscando)
      setAllClients?.([]);

      removeFromStorage("selected_client");
      removeFromStorage("new_data_sets");
      removeFromStorage("discounts");
      removeFromStorage("datasets");

      localStorage.removeItem("@RCA_APP:selected_client");
      localStorage.removeItem("localTime");

      // mantém seu comportamento atual (sem mexer no DB)
      await deleteDB();
    } catch (error) {
      console.error("Erro ao limpar os datasets:", error);
      throw error;
    }
  }, [deleteDB, setAllClients]);

  // Reconcile de boot:
  // - se tiver pending_selected_rca, remove pra não ficar “meio logado”
  // - parse correto de storages
  useEffect(() => {
    const userLogin = safeParse<any>(getOnStorage("user_login_data"), {});
    const aws_data = safeParse<any>(userLogin?.data?.RCALogin?.aws_data, {});
    const coordinatorRCAs = aws_data?.representante || [];

    const pending = normalizeMaybeObject<any>(getOnStorage("pending_selected_rca"));
    if (pending) {
      removeFromStorage("pending_selected_rca");
      removeFromStorage("selected_rca");
    }

    const selected = normalizeMaybeObject<any>(getOnStorage("selected_rca"));
    const logged = normalizeMaybeObject<any>(getOnStorage("user_logado"));

    setSelectedRCA(selected);
    setLoggedUser(logged);
    setHierarchy(coordinatorRCAs);
    setUserType(aws_data?.tipo_usuario || "");
  }, []);

  const handleRCALogin = useCallback(
    async (data: RCALoginInput): Promise<boolean> => {
      if (loginInFlightRef.current) return false;

      if (!data?.cod_pessoa) {
        console.warn("RCALogin: cod_pessoa ausente.");
        return false;
      }

      if (selectedRCA?.codPessoa && String(selectedRCA.codPessoa) === String(data.cod_pessoa)) {
        return true;
      }

      loginInFlightRef.current = true;
      openModalSync(true);
      setError(false);

      try {
        await clearOldDatasets();

        const rcaDataFull = hierarchyByCodPessoa.get(String(data.cod_pessoa));
        if (!rcaDataFull) throw new Error("RCA não encontrado na lista do coordenador.");
        if (!rcaDataFull.link_Dataset) throw new Error("link_Dataset não disponível para este RCA.");

        const dataRca = buildRcaData(data);

        // “pending” pra consistência
        setOnStorage("pending_selected_rca", dataRca);

        const formattedDate = getDate();
        setLastSync(formattedDate);

        const success = await loadFiles(rcaDataFull.link_Dataset);
        if (!success) throw new Error("Falha no download dos arquivos.");

        // commit
        removeFromStorage("pending_selected_rca");
        setOnStorage("selected_rca", dataRca);
        setOnStorage("rca_usuario", dataRca);
        setSelectedRCA(dataRca);

        if (navigator.serviceWorker?.controller) {
          navigator.serviceWorker.controller.postMessage({ type: "SW_CLEAR_RCA_DYNAMIC" });
        }

        window.location.href = "/";
        return true;
      } catch (error: any) {
        console.error("RCALogin falhou:", error?.message || error);
        setError(true);

        removeFromStorage("pending_selected_rca");
        removeFromStorage("selected_rca");
        removeFromStorage("rca_usuario");
        setSelectedRCA(null);

        return false;
      } finally {
        openModalSync(false);
        loginInFlightRef.current = false;
      }
    },
    [
      selectedRCA,
      clearOldDatasets,
      hierarchyByCodPessoa,
      loadFiles,
      getDate,
      setLastSync,
      openModalSync,
      setError,
    ]
  );

  const handleLogoutRCA = useCallback(async () => {
    if (loadingLogout) return;

    setLoadingLogout(true);
    openModalSync(true);
    setError(false);

    try {
      await clearOldDatasets();

      const logged = normalizeMaybeObject<any>(getOnStorage("user_logado"));
      if (logged) setOnStorage("rca_usuario", logged);

      removeFromStorage("selected_rca");
      removeFromStorage("pending_selected_rca");
      setSelectedRCA(null);

      window.location.reload();
    } catch (e) {
      console.error("Erro ao realizar logout:", e);
      setError(true);
    } finally {
      setLoadingLogout(false);
      openModalSync(false);
    }
  }, [loadingLogout, openModalSync, clearOldDatasets, setError]);

  return (
    <ClientHierarchyContext.Provider
      value={{
        hierarchy,
        userType,
        selectedRCA,
        loggedUser,
        isCoordinatorActive,
        loadingLogin: loading,
        loadingLogout,
        isOpenedModalCoordinator,
        setIsOpenedModalCoordinator,
        setHierarchy,
        handleRCALogin,
        handleLogoutRCA,
        setViewType,
      }}
    >
      {children}
    </ClientHierarchyContext.Provider>
  );
};

const useClientHierarchy = (): ClientHierarchyContextData => {
  const context = useContext(ClientHierarchyContext);
  if (!context) throw new Error("Erro ao usar hook useClientHierarchy");
  return context;
};

export { ClientHierarchyProvider, useClientHierarchy };
